/*
 * ANZIC audio player — persistent, single-instance Web Audio player.
 *
 * Two parts:
 *   1. window.AnzicPlayer — a singleton controller built on Howler.js. Holds the
 *      queue + playback state in JS memory (Web Audio, NO <audio> element), so the
 *      sound is decoupled from the DOM. On a full reload it is recreated and can
 *      resume from sessionStorage; in Phase 2 (soft nav) it simply never reloads.
 *   2. <audio-player> — the bottom bar custom element. Pure UI: it controls the
 *      singleton and re-renders from player:track / player:state / player:progress.
 *
 * Play affordances anywhere on the site are DECOUPLED from the controller. A
 * button carries data-play-album (+ optional data-track-index) and a sibling
 *   <script type="application/json" class="album-audio-data" data-album-id=...>
 * island holds the album's track metadata (see snippets/album-audio-data.liquid).
 * One delegated listener on document reads the island and starts playback, so
 * buttons in swapped-in content (Phase 2) keep working with no re-binding.
 *
 * Conventions (match latest-releases-hero.js): IIFE, requestAnimationFrame for
 * the progress fill (no CSS @keyframes), BEM-ish .ap__* class names.
 */
(() => {
  'use strict';

  const CONFIG = Object.assign(
    {
      signEndpoint: '', // e.g. '/apps/audio/sign' — returns { url, ttl } for a track_id (Phase 0 host)
      crossfadeMs: 300,
      previewSeconds: 0, // clips are cut + faded server-side — play them whole (set >0 only for full-track sources)
      previewFadeSeconds: 3, // only used when previewSeconds > 0: fade over the last N seconds before the cap
      // Resume-after-reload (restore the playing track + position across full page
      // loads). OFF for Phase 1 — each page load starts the player fresh. The whole
      // mechanism is preserved behind this flag for Phase 2 (soft-nav degraded mode):
      // set true here, or pass AnzicPlayerConfig.enableResume = true and re-push.
      enableResume: false,
      prefetchHoverMs: 120, // hover dwell before prefetching a track's signed URL
    },
    window.AnzicPlayerConfig || {}
  );

  const STORE_KEY = 'anzic_player_state';
  const VOL_KEY = 'anzic_player_volume';

  const ICONS = {
    play: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>',
    pause: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 5h4v14H6zm8 0h4v14h-4z"/></svg>',
    prev: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6h2v12H6zm13 0v12l-9-6z"/></svg>',
    next: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M16 6h2v12h-2zM5 6v12l9-6z"/></svg>',
    vol: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 9v6h4l5 5V4L7 9H3z"/></svg>',
    mute: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 9v6h4l5 5V4L7 9H3zm18 .4L19.6 8 17 10.6 14.4 8 13 9.4 15.6 12 13 14.6 14.4 16 17 13.4 19.6 16 21 14.6 18.4 12z"/></svg>',
    close: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M19 6.4 17.6 5 12 10.6 6.4 5 5 6.4 10.6 12 5 17.6 6.4 19 12 13.4 17.6 19 19 17.6 13.4 12z"/></svg>',
  };

  const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
  const fmtTime = (s) => {
    s = Math.max(0, Math.floor(s || 0));
    const m = Math.floor(s / 60);
    return m + ':' + String(s % 60).padStart(2, '0');
  };

  /* ----------------------------------------------------------------------- */
  /* Singleton controller                                                    */
  /* ----------------------------------------------------------------------- */
  class PlayerController {
    constructor() {
      this.album = null; // { id, title, artist, cover, url }
      this.queue = []; // playable tracks: [{ n, title, duration, track_id|url, listIndex }]
      this.index = -1; // current index into queue
      this.howl = null;
      this.isPlaying = false;
      this._loading = false; // true between a track's request and its onplay (spinner)
      this.volume = this._loadVolume();
      this._lastVolume = this.volume || 0.8;
      this._pendingSeek = 0; // seconds to seek to once the next track starts
      this._urlCache = new Map(); // track_id -> { url, exp }
      this._raf = null;
      this.previewSeconds = CONFIG.previewSeconds;
      this.previewFadeSeconds = CONFIG.previewFadeSeconds;
      this._previewFading = false;

      // One delegated listener for every play button on the page (now and future).
      document.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-play-album]');
        if (!btn) return;
        e.preventDefault();
        this._onPlayButton(btn);
      });
      // Decoupled programmatic entry point.
      document.addEventListener('player:play', (e) => {
        const d = e.detail || {};
        if (d.album) this.playAlbum(d.album, d.startIndex || 0);
      });

      // Resume-after-reload bridge — Phase 2 only (see CONFIG.enableResume). In Phase 1
      // each page load starts fresh: skip persistence + restore, and clear any state a
      // prior build may have left behind so toggling the flag later starts clean.
      if (CONFIG.enableResume) {
        window.addEventListener('pagehide', () => this._persist());
        document.addEventListener('visibilitychange', () => {
          if (document.hidden) this._persist();
        });
        this._restore();
      } else {
        try {
          sessionStorage.removeItem(STORE_KEY);
        } catch (_) {
          /* private mode — ignore */
        }
      }

      // First-play acceleration: keep the (cold-start-prone) sign round-trip out of the
      // click→sound path by resolving + caching signed URLs ahead of time — on pointer
      // intent and once on load for the page's primary album.
      this._prefetching = new Set();
      this._installPrefetch();
      this._warmUpPrimaryAlbum();
    }

    _loadVolume() {
      const v = parseFloat(localStorage.getItem(VOL_KEY));
      return isNaN(v) ? 0.8 : clamp(v, 0, 1);
    }

    _onPlayButton(btn) {
      const albumId = btn.dataset.playAlbum;
      const isTrackBtn = btn.hasAttribute('data-track-index');
      const startIndex = parseInt(btn.dataset.trackIndex || '0', 10);

      if (this.album && String(this.album.id) === String(albumId)) {
        if (!isTrackBtn) return this.toggle(); // album/card button → toggle this album
        const cur = this.currentTrack;
        if (cur && cur.listIndex === startIndex) return this.toggle(); // same track → toggle
      }

      const island = document.querySelector(
        '.album-audio-data[data-album-id="' + (window.CSS && CSS.escape ? CSS.escape(albumId) : albumId) + '"]'
      );
      if (!island) return;
      let data;
      try {
        data = JSON.parse(island.textContent);
      } catch (_) {
        return;
      }
      this.playAlbum(data, startIndex);
    }

    playAlbum(album, startIndex) {
      // titleSuffix: set by album-audio-data when EVERY title ends in it (" - Live" on live
      // albums) — the page's tracklist hides it, so the player bar does too.
      const sfx = album.titleSuffix;
      const tracks = (album.tracks || [])
        .map((t, i) => Object.assign({}, t, { listIndex: i },
          sfx && t.title && t.title.endsWith(sfx) ? { title: t.title.slice(0, -sfx.length) } : null))
        .filter((t) => t.playable !== false && (t.track_id || t.url));
      if (!tracks.length) return;

      this.album = { id: album.id, title: album.title, artist: album.artist, cover: album.cover, url: album.url };
      this.queue = tracks;
      let qi = this.queue.findIndex((t) => t.listIndex >= (startIndex || 0));
      if (qi < 0) qi = 0;
      this._playIndex(qi);
    }

    async _playIndex(qi, startPos) {
      if (typeof Howl === 'undefined') return; // howler.min.js failed to load
      if (qi < 0 || qi >= this.queue.length) return;
      const track = this.queue[qi];

      // Mark current + loading immediately so the track row shows the spinner the
      // moment it's clicked — instant "click registered" feedback, before the URL
      // resolves and the audio buffer loads.
      this.index = qi;
      this._loading = true;
      this.syncButtons();

      let url;
      try {
        url = await this._resolveUrl(track);
      } catch (_) {
        url = null;
      }
      if (!url) {
        this._loading = false;
        this.syncButtons();
        this.next();
        return;
      }

      const oldHowl = this.howl;
      this._pendingSeek = startPos || 0;
      const howl = new Howl({
        src: [url],
        html5: false, // Web Audio path — no <audio> element in the DOM
        volume: 0,
        onplay: () => {
          this._loading = false;
          this.isPlaying = true;
          if (this._pendingSeek) {
            howl.seek(this._pendingSeek);
            this._pendingSeek = 0;
          }
          this._startRaf();
          this._emitState();
        },
        onpause: () => {
          this.isPlaying = false;
          this._stopRaf();
          this._persist();
          this._emitState();
        },
        onend: () => this.next(),
        onloaderror: () => {
          this._loading = false;
          this.next();
        },
        onplayerror: () => {
          howl.once('unlock', () => howl.play());
        },
      });

      this.howl = howl;
      this._previewFading = false;
      howl.play();
      howl.fade(0, this.volume, CONFIG.crossfadeMs);
      if (oldHowl) {
        oldHowl.fade(oldHowl.volume(), 0, CONFIG.crossfadeMs);
        oldHowl.once('fade', () => {
          oldHowl.stop();
          oldHowl.unload();
        });
      }

      this._emitTrack();
      this._setMediaSession();
      this._persist();
    }

    async _resolveUrl(track) {
      if (track.url) return track.url; // dev / public-host path (URL present in island)
      if (!track.track_id) return null;
      const cached = this._urlCache.get(track.track_id);
      if (cached && cached.exp > Date.now() + 5000) return cached.url;
      if (!CONFIG.signEndpoint) return null;
      const res = await fetch(CONFIG.signEndpoint + '?track=' + encodeURIComponent(track.track_id), {
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) return null;
      const json = await res.json();
      if (!json || !json.url) return null;
      this._urlCache.set(track.track_id, { url: json.url, exp: Date.now() + (json.ttl ? json.ttl * 1000 : 55000) });
      return json.url;
    }

    /* --- First-play acceleration: prefetch signed URLs ahead of the click ------ */

    // Resolve + cache a track's signed URL ahead of play. Best-effort and deduped:
    // skips dev/public-URL tracks (URL already present), already-cached URLs, and
    // in-flight fetches. Populates the same _urlCache the play path reads, so a click
    // that follows finds a warm cache and never waits on the sign endpoint.
    _prefetch(track) {
      if (!track || track.url || !track.track_id || !CONFIG.signEndpoint) return;
      const id = track.track_id;
      const cached = this._urlCache.get(id);
      if (cached && cached.exp > Date.now() + 5000) return;
      if (this._prefetching.has(id)) return;
      this._prefetching.add(id);
      this._resolveUrl(track)
        .catch(() => null)
        .then(() => this._prefetching.delete(id));
    }

    // The track a play button's click would START (mirrors playAlbum's start pick):
    // the album's first playable track, or — for a track-row button — the first
    // playable track at/after its index. Reads the button's data island; no playback.
    _startTrackForButton(btn) {
      const albumId = btn.dataset.playAlbum;
      const island = document.querySelector(
        '.album-audio-data[data-album-id="' + (window.CSS && CSS.escape ? CSS.escape(albumId) : albumId) + '"]'
      );
      if (!island) return null;
      let data;
      try {
        data = JSON.parse(island.textContent);
      } catch (_) {
        return null;
      }
      const tracks = (data.tracks || [])
        .map((t, i) => Object.assign({}, t, { listIndex: i }))
        .filter((t) => t.playable !== false && (t.track_id || t.url));
      if (!tracks.length) return null;
      let qi = 0;
      if (btn.hasAttribute('data-track-index')) {
        const startIndex = parseInt(btn.dataset.trackIndex || '0', 10);
        qi = tracks.findIndex((t) => t.listIndex >= startIndex);
        if (qi < 0) qi = 0;
      }
      return tracks[qi];
    }

    // Prefetch on pointer intent: a short hover dwell (desktop) and pointerdown (the
    // press preceding every click, incl. touch). Delegated on document so it also
    // covers buttons added later. Cheap: deduped, and hover is debounced.
    _installPrefetch() {
      if (!CONFIG.signEndpoint) return; // dev / public-URL host: nothing to pre-sign
      const onIntent = (btn) => {
        const t = this._startTrackForButton(btn);
        if (t) this._prefetch(t);
      };
      let hoverBtn = null;
      let hoverTimer = null;
      const clearHover = () => {
        if (hoverTimer) clearTimeout(hoverTimer);
        hoverTimer = null;
        hoverBtn = null;
      };
      document.addEventListener('pointerover', (e) => {
        const btn = e.target.closest('[data-play-album]');
        if (!btn || btn === hoverBtn) return;
        clearHover();
        hoverBtn = btn;
        hoverTimer = setTimeout(() => {
          if (hoverBtn === btn) onIntent(btn);
        }, CONFIG.prefetchHoverMs);
      });
      document.addEventListener('pointerout', (e) => {
        const btn = e.target.closest('[data-play-album]');
        if (btn && btn === hoverBtn) clearHover();
      });
      document.addEventListener('pointerdown', (e) => {
        const btn = e.target.closest('[data-play-album]');
        if (btn) onIntent(btn);
      });
    }

    // On load, warm the sign function + cache the first playable track of the page's
    // PRIMARY album — the first data island in document order (the product's own album
    // on a product page; the first card elsewhere). Removes cold-start from the most
    // likely first play. Runs at idle so it never competes with page render.
    _warmUpPrimaryAlbum() {
      if (!CONFIG.signEndpoint) return;
      const run = () => {
        const island = document.querySelector('.album-audio-data');
        if (!island) return;
        let data;
        try {
          data = JSON.parse(island.textContent);
        } catch (_) {
          return;
        }
        const first = (data.tracks || []).find((t) => t.playable !== false && (t.track_id || t.url));
        if (first) this._prefetch(first);
      };
      if ('requestIdleCallback' in window) requestIdleCallback(run, { timeout: 2500 });
      else setTimeout(run, 800);
    }

    toggle() {
      if (this.isPlaying) this.pause();
      else this.play();
    }
    play() {
      if (this.howl) {
        if (!this.isPlaying) this.howl.play();
        return;
      }
      if (this.currentTrack) this._playIndex(this.index, this._restoredPos || 0); // resume-after-reload
    }
    pause() {
      if (this.howl && this.isPlaying) this.howl.pause();
    }
    next() {
      if (this.index < this.queue.length - 1) this._playIndex(this.index + 1);
      else this._end();
    }
    prev() {
      if (this.howl && this.howl.seek() > 3) {
        this.howl.seek(0);
        return;
      }
      if (this.index > 0) this._playIndex(this.index - 1);
    }
    seek(fraction) {
      if (!this.howl) return;
      const eff = this._effectiveDuration() || this.howl.duration() || 0;
      this.howl.seek(eff * clamp(fraction, 0, 1));
      this._previewFading = false; // re-arm the preview fade if scrubbed back
      if (this.howl.volume() < this.volume) this.howl.volume(this.volume); // undo a prior fade
      this._emitProgress();
    }
    setVolume(v) {
      this.volume = clamp(v, 0, 1);
      if (this.volume > 0) this._lastVolume = this.volume;
      if (this.howl) this.howl.volume(this.volume);
      localStorage.setItem(VOL_KEY, String(this.volume));
      this._emitState();
    }
    toggleMute() {
      this.setVolume(this.volume > 0 ? 0 : this._lastVolume || 0.8);
    }
    clear() {
      this._stopRaf();
      if (this.howl) {
        this.howl.stop();
        this.howl.unload();
        this.howl = null;
      }
      this.album = null;
      this.queue = [];
      this.index = -1;
      this.isPlaying = false;
      this._restoredPos = 0;
      sessionStorage.removeItem(STORE_KEY);
      this._emitTrack();
      this._emitState();
    }
    _end() {
      this._stopRaf();
      this.isPlaying = false;
      if (this.howl) this.howl.seek(0);
      this._emitState();
    }

    get currentTrack() {
      return this.index >= 0 ? this.queue[this.index] : null;
    }
    get position() {
      return this.howl ? this.howl.seek() || 0 : this._restoredPos || 0;
    }
    get duration() {
      return this.howl ? this.howl.duration() || 0 : 0;
    }
    get hasPrev() {
      return this.index > 0 || !!(this.howl && this.howl.seek() > 3);
    }
    get hasNext() {
      return this.index < this.queue.length - 1;
    }

    _startRaf() {
      if (this._raf) return;
      const tick = () => {
        this._enforcePreview();
        this._emitProgress();
        this._updateTrackRing();
        const now = Date.now();
        if (!this._lastPersist || now - this._lastPersist > 3000) {
          this._lastPersist = now;
          this._persist(); // keep saved position ~current for resume-after-reload
        }
        this._raf = this.isPlaying ? requestAnimationFrame(tick) : null;
      };
      this._raf = requestAnimationFrame(tick);
    }
    _stopRaf() {
      if (this._raf) {
        cancelAnimationFrame(this._raf);
        this._raf = null;
      }
    }

    _emitState() {
      document.dispatchEvent(
        new CustomEvent('player:state', {
          detail: {
            isPlaying: this.isPlaying,
            hasPrev: this.hasPrev,
            hasNext: this.hasNext,
            volume: this.volume,
          },
        })
      );
      this.syncButtons();
    }
    _emitTrack() {
      document.dispatchEvent(
        new CustomEvent('player:track', { detail: { album: this.album, track: this.currentTrack } })
      );
    }
    // Effective (capped) duration: the 30s preview window, or the real duration
    // if the track is shorter than the cap. previewSeconds=0 disables the cap.
    _effectiveDuration() {
      const full = this.howl ? this.howl.duration() || 0 : 0;
      if (this.previewSeconds && full > this.previewSeconds) return this.previewSeconds;
      return full;
    }

    // Enforce the 30s preview: fade out approaching the cap, then advance at it.
    // Tracks shorter than the cap end naturally via Howl's onend (no double-fire).
    _enforcePreview() {
      if (!this.howl || !this.isPlaying || !this.previewSeconds) return;
      const full = this.howl.duration() || 0;
      if (!full || full <= this.previewSeconds) return;
      const pos = this.howl.seek() || 0;
      const limit = this.previewSeconds;
      const fadeStart = limit - this.previewFadeSeconds;
      if (pos < fadeStart) {
        this._previewFading = false;
        return;
      }
      if (!this._previewFading && this.previewFadeSeconds > 0) {
        this._previewFading = true;
        const ms = Math.max(50, (limit - pos) * 1000); // land at 0 right at the cap
        this.howl.fade(this.howl.volume(), 0, ms);
      }
      if (pos >= limit) {
        this._previewFading = false;
        this.next(); // advance to the next preview, or stop at album end
      }
    }

    _emitProgress() {
      const eff = this._effectiveDuration();
      let pos = this.position;
      if (eff) pos = Math.min(pos, eff);
      document.dispatchEvent(new CustomEvent('player:progress', { detail: { position: pos, duration: eff } }));
    }

    // Drive the inline progress ring on the active track row(s). Cheap: the selector
    // matches 0–1 elements, and this runs only while playing (from the rAF tick).
    _updateTrackRing() {
      const eff = this._effectiveDuration();
      const frac = eff ? clamp(Math.min(this.position, eff) / eff, 0, 1) : 0;
      document
        .querySelectorAll('.audio-play-btn--track.is-current')
        .forEach((b) => b.style.setProperty('--ring', frac.toFixed(4)));
    }

    // Derive every play button's icon state from the controller (single source of truth).
    syncButtons(root) {
      const scope = root || document;
      scope.querySelectorAll('[data-play-album]').forEach((btn) => {
        const sameAlbum = this.album && String(btn.dataset.playAlbum) === String(this.album.id);
        const isTrackBtn = btn.hasAttribute('data-track-index');
        const ti = parseInt(btn.dataset.trackIndex || '0', 10);
        let current;
        if (isTrackBtn) current = !!(sameAlbum && this.currentTrack && this.currentTrack.listIndex === ti);
        else current = !!sameAlbum;
        btn.classList.toggle('is-current', current);
        btn.classList.toggle('is-playing', current && this.isPlaying);
        btn.classList.toggle('is-loading', current && !!this._loading);
        // aria-pressed is only valid on real buttons; play affordances can also be
        // plain elements (e.g. the clickable track title is a <span>).
        if (btn.tagName === 'BUTTON') {
          btn.setAttribute('aria-pressed', current && this.isPlaying ? 'true' : 'false');
        }
      });
    }

    _setMediaSession() {
      if (!('mediaSession' in navigator)) return;
      const t = this.currentTrack;
      const a = this.album;
      if (!t || !a) return;
      try {
        navigator.mediaSession.metadata = new MediaMetadata({
          title: t.title || '',
          artist: a.artist || '',
          album: a.title || '',
          artwork: a.cover ? [{ src: a.cover, sizes: '512x512', type: 'image/jpeg' }] : [],
        });
        navigator.mediaSession.setActionHandler('play', () => this.play());
        navigator.mediaSession.setActionHandler('pause', () => this.pause());
        navigator.mediaSession.setActionHandler('previoustrack', () => this.prev());
        navigator.mediaSession.setActionHandler('nexttrack', () => this.next());
      } catch (_) {
        /* no-op */
      }
    }

    _persist() {
      if (!CONFIG.enableResume) return; // Phase 1: no resume-after-reload
      try {
        sessionStorage.setItem(
          STORE_KEY,
          JSON.stringify({
            album: this.album,
            queue: this.queue,
            index: this.index,
            position: this.position,
            volume: this.volume,
          })
        );
      } catch (_) {
        /* quota / private mode — ignore */
      }
    }

    // Resume-after-reload: restore the bar in a PAUSED state at the saved position.
    // Audio only (re)starts on a user gesture (autoplay policy), satisfied by the
    // play button or the bar's play control.
    _restore() {
      let s;
      try {
        s = JSON.parse(sessionStorage.getItem(STORE_KEY));
      } catch (_) {
        return;
      }
      if (!s || !s.album || !Array.isArray(s.queue) || !s.queue.length) return;
      this.album = s.album;
      this.queue = s.queue;
      this.index = typeof s.index === 'number' ? s.index : 0;
      this._restoredPos = s.position || 0;
      this.isPlaying = false;
      // Defer the first paint until the bar element is connected (it reads us on connect).
    }
  }

  window.AnzicPlayer = window.AnzicPlayer || new PlayerController();

  /* ----------------------------------------------------------------------- */
  /* Bottom bar UI                                                           */
  /* ----------------------------------------------------------------------- */
  class AudioPlayerBar extends HTMLElement {
    connectedCallback() {
      if (this._built) return;
      this._built = true;
      this.innerHTML = this._template();
      this._cache();
      this._bind();

      const P = window.AnzicPlayer;
      if (P.currentTrack) {
        // Restored or already-playing state — paint immediately.
        this._renderTrack({ album: P.album, track: P.currentTrack });
        this._renderState({ isPlaying: P.isPlaying, hasPrev: P.hasPrev, hasNext: P.hasNext, volume: P.volume });
        this._renderProgress({ position: P.position, duration: P.duration });
      }
      P.syncButtons();
    }

    _template() {
      return (
        '<div class="ap" role="region" aria-label="Audio player" hidden>' +
        '<input class="ap__seek" type="range" min="0" max="1000" value="0" step="1" aria-label="Seek" style="--seek:0">' +
        '<div class="ap__row">' +
        '<div class="ap__now">' +
        '<span class="ap__meta"><a class="ap__title" href="#"></a><span class="ap__artist"></span></span>' +
        '</div>' +
        '<div class="ap__controls">' +
        '<button type="button" class="ap__btn ap__prev" aria-label="Previous track">' + ICONS.prev + '</button>' +
        '<button type="button" class="ap__btn ap__playpause" aria-label="Play">' + ICONS.play + '</button>' +
        '<button type="button" class="ap__btn ap__next" aria-label="Next track">' + ICONS.next + '</button>' +
        '</div>' +
        '<div class="ap__vol">' +
        '<button type="button" class="ap__btn ap__mute" aria-label="Mute">' + ICONS.vol + '</button>' +
        '<input class="ap__volrange" type="range" min="0" max="100" value="80" aria-label="Volume">' +
        '</div>' +
        '<button type="button" class="ap__btn ap__close" aria-label="Close player">' + ICONS.close + '</button>' +
        '</div>' +
        '</div>'
      );
    }

    _cache() {
      this.root = this.querySelector('.ap');
      this.elSeek = this.querySelector('.ap__seek');
      this.elPlay = this.querySelector('.ap__playpause');
      this.elPrev = this.querySelector('.ap__prev');
      this.elNext = this.querySelector('.ap__next');
      this.elMute = this.querySelector('.ap__mute');
      this.elVol = this.querySelector('.ap__volrange');
      this.elTitle = this.querySelector('.ap__title');
      this.elArtist = this.querySelector('.ap__artist');
    }

    _bind() {
      const P = window.AnzicPlayer;
      this.elPlay.addEventListener('click', () => P.toggle());
      this.elPrev.addEventListener('click', () => P.prev());
      this.elNext.addEventListener('click', () => P.next());
      this.elMute.addEventListener('click', () => P.toggleMute());
      this.querySelector('.ap__close').addEventListener('click', () => {
        P.clear();
        this._hide();
      });

      this.elSeek.addEventListener('input', () => {
        this._scrubbing = true;
        P.seek(this.elSeek.value / 1000);
      });
      this.elSeek.addEventListener('change', () => {
        this._scrubbing = false;
      });

      this.elVol.value = Math.round(P.volume * 100);
      this.elVol.addEventListener('input', () => P.setVolume(this.elVol.value / 100));

      document.addEventListener('player:track', (e) => this._renderTrack(e.detail));
      document.addEventListener('player:state', (e) => this._renderState(e.detail));
      document.addEventListener('player:progress', (e) => this._renderProgress(e.detail));
    }

    _show() {
      if (!this.root.hidden) return;
      this.root.hidden = false;
      document.body.classList.add('has-audio-player');
      requestAnimationFrame(() => this.root.classList.add('is-visible'));
    }
    _hide() {
      this.root.classList.remove('is-visible');
      document.body.classList.remove('has-audio-player');
      const root = this.root;
      setTimeout(() => {
        if (!root.classList.contains('is-visible')) root.hidden = true;
      }, 340);
    }

    _renderTrack(d) {
      if (!d || !d.track) {
        return;
      }
      this._show();
      const a = d.album || {};
      const t = d.track;
      this.elTitle.textContent = t.title || a.title || '';
      this.elTitle.href = a.url || '#';
      this.elArtist.textContent = a.artist || '';
    }
    _renderState(d) {
      this.elPlay.innerHTML = d.isPlaying ? ICONS.pause : ICONS.play;
      this.elPlay.setAttribute('aria-label', d.isPlaying ? 'Pause' : 'Play');
      this.elPrev.toggleAttribute('disabled', !d.hasPrev);
      this.elNext.toggleAttribute('disabled', !d.hasNext);
      this.elMute.innerHTML = d.volume > 0 ? ICONS.vol : ICONS.mute;
      if (document.activeElement !== this.elVol) this.elVol.value = Math.round((d.volume || 0) * 100);
    }
    _renderProgress(d) {
      const frac = d.duration ? d.position / d.duration : 0;
      if (!this._scrubbing) {
        this.elSeek.value = Math.round(frac * 1000);
        this.elSeek.style.setProperty('--seek', frac.toFixed(4));
      }
    }
  }

  if (!customElements.get('audio-player')) customElements.define('audio-player', AudioPlayerBar);
})();
